import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Store } from 'lucide-react';
import { MerchantCard } from './MerchantCard';
import type { Merchant } from '@/lib/merchants';

interface MerchantListProps {
  merchants: Merchant[];
  isLoading: boolean;
  selectedMerchant: Merchant | null;
  onSelectMerchant: (merchant: Merchant) => void;
}

export function MerchantList({
  merchants,
  isLoading,
  selectedMerchant,
  onSelectMerchant,
}: MerchantListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="py-4 px-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-full" />
                  <div className="flex gap-1">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                  <div className="flex gap-3">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </div>
                <Skeleton className="h-5 w-14 rounded-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (merchants.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center space-y-4">
          <p className="text-muted-foreground max-w-sm mx-auto">
            No merchants found. Try adjusting your filters, or add your own business to put it on the map.
          </p>
          <Button asChild variant="outline" size="sm" className="gap-2 border-bch/30 hover:bg-bch/5 hover:border-bch/50">
            <a href="https://bchnostr.com/merchants" target="_blank" rel="noopener noreferrer">
              <Store className="size-4 text-bch" />
              Add your business on BCHNostr
            </a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-2 pr-2">
        {merchants.map((merchant) => (
          <MerchantCard
            key={merchant.event.id}
            merchant={merchant}
            isSelected={selectedMerchant?.pubkey === merchant.pubkey}
            onClick={() => onSelectMerchant(merchant)}
          />
        ))}
      </div>
    </ScrollArea>
  );
}
